/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { FC, useMemo } from 'react'

import { useHistory } from 'react-router-dom'

import { Channel } from '@at/sauron-platform-core'
import { SEARCH_URL_STORAGE_KEY } from '@at/window-utils/lib/local-storage-utils/local-storage-keys'
import { setItemLocalStorage } from '@at/window-utils/lib/local-storage-utils/local-storage.utils'

import { ChildrenProp, HistoryProp, HistoryState, SearchQuery } from './types'
import { getChannelFromPathnameOrAdvertisingLocation } from '../../utils/channel-utilities'
import { purgeCache } from '../../graphql/cache'
import useStoreSelectedSearchFilters from '../../utils/search-filters-local-storage/search-filters-local-storage'

type SearchPage = SearchQuery &
    HistoryProp & {
        replacePage: (page: number) => void
        removeHash: () => void
        updateSort: (sort: string) => void
        removeTransientParams: () => void
    }

const useSearchQuery = () => {
    const history = useHistory<HistoryState>()
    const { search, pathname, hash } = history.location
    const channel = getChannelFromPathnameOrAdvertisingLocation(pathname, search)
    return useMemo(() => {
        const queryParams = new URLSearchParams(search)

        return {
            queryParams,
            search,
            pathname,
            channel,
            history,
            hash,
        }
    }, [search, channel, pathname, history, hash])
}

const SearchPageContext = React.createContext<SearchPage>({
    channel: Channel.cars,
    search: '?',
    queryParams: new URLSearchParams('?'),
    scrollToLastPosition: () => {},
} as unknown as SearchPage)

interface SearchPageProps extends ChildrenProp {
    isSkeletonPage: boolean
}

const SearchPage: FC<SearchPageProps> = ({ isSkeletonPage, children }) => {
    const { history, pathname, queryParams, search, channel } = useSearchQuery()

    function mergeParams(params: { [p: string]: string | undefined }) {
        const nextParams = new URLSearchParams(history.location.search)
        for (const [name, value] of Object.entries(params)) {
            if (value !== undefined) {
                nextParams.set(name, value)
            } else {
                nextParams.delete(name)
            }
        }

        return nextParams
    }

    const updateUrl = (params: { [p: string]: string | undefined }, replace = false) => {
        if (history) {
            const url = `${pathname}?${mergeParams(params).toString()}`
            if (!replace) {
                purgeCache()
                history?.push(url)
            } else {
                history.replace(url)
            }
            setItemLocalStorage(SEARCH_URL_STORAGE_KEY, url)
        }
    }

    const noOpFunction = () => {}

    const value = {
        channel,
        search,
        queryParams,
        history,
        replacePage: (page: number) => {
            updateUrl({ page: String(page) }, true)
        },
        removeTransientParams: isSkeletonPage
            ? noOpFunction
            : () => {
                  if (
                      queryParams.has('showSaveSearch') ||
                      queryParams.has('showSearchForm') ||
                      queryParams.has('showSavedSearches')
                  ) {
                      updateUrl(
                          { showSaveSearch: undefined, showSearchForm: undefined, showSavedSearches: undefined },
                          true
                      )
                  }
              },
    } as unknown as SearchPage

    useStoreSelectedSearchFilters()

    return <SearchPageContext.Provider value={value}>{children}</SearchPageContext.Provider>
}

const useSearchPage = () => {
    return React.useContext(SearchPageContext)
}

export { SearchPage, useSearchPage }
